import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { ReferenceLayerView } from '../../hooks/useReferenceLayers';
import {
  REFERENCE_LAYER_TYPE_LABELS,
  type ReferenceGeometryKind,
  type ReferenceLayerStyle,
} from '../../services/referenceLayerApi';
import { referenceLegend } from './ReferenceLayersMapLayers';

/** Keys shown before the list is folded behind "+N more". */
const MAX_CATEGORY_ROWS = 10;

/**
 * Swatch that mirrors how the layer is drawn: filled box with outline for
 * polygons, a stroke for lines, a dot for points. `color` overrides the
 * layer's base colour (used for per-category rows).
 */
export const ReferenceSwatch: React.FC<{
  kind: ReferenceGeometryKind;
  style: ReferenceLayerStyle;
  color?: string;
  size?: number;
}> = ({ kind, style, color, size = 14 }) => {
  const fill = color ?? style.color;
  const stroke = color ?? style.stroke_color;
  if (kind === 'line') {
    return (
      <Box
        sx={{
          width: size + 4,
          height: 0,
          borderTop: `${Math.max(1.5, Math.min(3, style.stroke_width))}px solid ${stroke}`,
          opacity: style.stroke_opacity,
        }}
      />
    );
  }
  if (kind === 'point') {
    const d = Math.max(8, Math.min(size, style.point_radius * 2));
    return (
      <Box
        sx={{
          width: d,
          height: d,
          borderRadius: '50%',
          bgcolor: fill,
          opacity: style.point_opacity,
          border: `1px solid ${stroke}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
        }}
      />
    );
  }
  // polygon / mixed
  return (
    <Box sx={{ position: 'relative', width: size + 2, height: size - 2 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: fill,
          opacity: style.fill_opacity,
          borderRadius: '2px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          border: `${Math.max(1, Math.min(2, style.stroke_width))}px solid ${stroke}`,
          borderRadius: '2px',
          opacity: style.stroke_opacity,
        }}
      />
      {kind === 'mixed' && (
        <Box
          sx={{
            position: 'absolute',
            right: -3,
            bottom: -3,
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: fill,
            border: `1px solid ${stroke}`,
          }}
        />
      )}
    </Box>
  );
};

type ItemProps = {
  view: ReferenceLayerView;
  /** Row text colour overrides for hosts with their own palette (COP). */
  textColor?: string;
  secondaryColor?: string;
};

/**
 * One reference layer as it appears on the map: name, type / count, and —
 * when the admin or user coloured it by a field — the category swatches or
 * the numeric ramp. Derived from the same rules the map layers use.
 */
export const ReferenceLayerLegendItem: React.FC<ItemProps> = ({ view, textColor, secondaryColor }) => {
  const theme = useTheme();
  const { layer, style } = view;
  const primary = textColor ?? theme.palette.text.primary;
  const secondary = secondaryColor ?? theme.palette.text.secondary;
  const entries = referenceLegend(layer, style);
  const colorBy = style.color_by;
  const isRamp = Boolean(colorBy && colorBy.mode === 'ramp' && entries.length > 1 && layer.fields[colorBy.field]?.kind === 'number');
  const typeLabel = REFERENCE_LAYER_TYPE_LABELS[layer.layer_type] ?? layer.layer_type;
  const sublabel = `${typeLabel} · ${layer.feature_count.toLocaleString()} feature${layer.feature_count === 1 ? '' : 's'}`;

  const headerSwatch = colorBy && entries.length > 0
    ? // Coloured by field: header shows the outline only; rows carry the colours.
      <ReferenceSwatch kind={layer.geometry_kind} style={{ ...style, fill_opacity: 0.12 }} color={undefined} />
    : <ReferenceSwatch kind={layer.geometry_kind} style={style} />;

  const visibleRows = isRamp ? [] : entries.slice(0, MAX_CATEGORY_ROWS);
  const hiddenRows = isRamp ? 0 : Math.max(0, entries.length - MAX_CATEGORY_ROWS);

  return (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {headerSwatch}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: '11px', fontWeight: 500, color: primary, display: 'block', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            title={layer.description || layer.name}
          >
            {layer.name}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '10px', color: secondary, display: 'block', lineHeight: 1.2 }}>
            {sublabel}
            {view.loading ? ' · loading…' : ''}
            {view.error ? ' · failed to load' : ''}
          </Typography>
        </Box>
      </Box>

      {colorBy && isRamp && (
        <Box sx={{ pl: 4.5, pr: 0.5, mt: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '9.5px', color: secondary, display: 'block', mb: 0.25 }}>
            {colorBy.field}
          </Typography>
          <Box
            sx={{
              height: 8,
              borderRadius: 0.5,
              background: `linear-gradient(to right, ${entries.map((e) => e.color).join(', ')})`,
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" sx={{ fontSize: '9px', color: secondary }}>
              {entries[0].label}
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '9px', color: secondary }}>
              {entries[entries.length - 1].label}
            </Typography>
          </Box>
        </Box>
      )}

      {colorBy && !isRamp && visibleRows.length > 0 && (
        <Box sx={{ pl: 4.5, mt: 0.25 }}>
          <Typography variant="caption" sx={{ fontSize: '9.5px', color: secondary, display: 'block', mb: 0.25 }}>
            {colorBy.field}
          </Typography>
          {visibleRows.map((e) => (
            <Box key={e.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.2 }}>
              <Box sx={{ width: 16, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <ReferenceSwatch kind={layer.geometry_kind} style={style} color={e.color} size={11} />
              </Box>
              <Typography
                variant="caption"
                sx={{ fontSize: '10px', color: primary, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={e.label}
              >
                {e.label}
              </Typography>
            </Box>
          ))}
          {hiddenRows > 0 && (
            <Typography variant="caption" sx={{ fontSize: '9.5px', color: secondary, display: 'block', pl: 3.25, pt: 0.2 }}>
              +{hiddenRows} more
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

type ListProps = {
  layers: ReferenceLayerView[];
  textColor?: string;
  secondaryColor?: string;
};

/** All enabled reference layers, in the order the admin sorted them. */
const ReferenceLayersLegend: React.FC<ListProps> = ({ layers, textColor, secondaryColor }) => (
  <>
    {layers.map((v) => (
      <ReferenceLayerLegendItem key={v.layer.id} view={v} textColor={textColor} secondaryColor={secondaryColor} />
    ))}
  </>
);

export default ReferenceLayersLegend;
